/* Global notification service for the admin panel.
 *
 * One entry point — `notify()` — does two things:
 *   1. Always shows an in-app toast (rendered by NotificationProvider).
 *   2. When `system: true`, ALSO fires a native/OS notification — but only
 *      while the tab is hidden, so you get a real "mobile" ping when you've
 *      switched away, and just a clean toast when you're already looking.
 *
 * Framework-agnostic (callable from socket handlers and plain modules, not just
 * React components) so the WhatsApp realtime events and the Automation Center
 * operations can all funnel through the same path.
 */

const MUTE_KEY = 'notif_muted';

let toastHandler = null;          // set by NotificationProvider once mounted
const pendingToasts = [];         // toasts emitted before the provider mounted
const permissionListeners = new Set();

export const isSupported = () => typeof window !== 'undefined' && 'Notification' in window;
export const getPermission = () => (isSupported() ? Notification.permission : 'unsupported');

export const isMuted = () => {
  try { return localStorage.getItem(MUTE_KEY) === '1'; } catch (e) { return false; }
};
export const setMuted = (v) => {
  try { localStorage.setItem(MUTE_KEY, v ? '1' : '0'); } catch (e) { /* ignore */ }
  emitPermissionChange();
};

// Let the navbar bell re-render when permission / mute state changes.
export const onPermissionChange = (fn) => {
  permissionListeners.add(fn);
  return () => permissionListeners.delete(fn);
};
const emitPermissionChange = () => permissionListeners.forEach((fn) => { try { fn(); } catch (e) { /* ignore */ } });

export const requestPermission = async () => {
  if (!isSupported()) return 'unsupported';
  try {
    const res = await Notification.requestPermission();
    emitPermissionChange();
    return res;
  } catch (e) {
    return getPermission();
  }
};

// Wired by NotificationProvider; flushes anything queued before mount.
export const _setToastHandler = (fn) => {
  toastHandler = fn;
  if (fn && pendingToasts.length) pendingToasts.splice(0).forEach((t) => fn(t));
};

const fallbackNotification = (title, options) => {
  try {
    const n = new Notification(title, options);
    n.onclick = () => {
      window.focus();
      const url = options?.data?.url;
      if (url) window.location.assign(url);
      n.close();
    };
  } catch (e) {
    // Android Chrome throws on the Notification constructor (SW-only) — ignore;
    // the service-worker path below covers that platform in production.
  }
};

const showSystemNotification = ({ title, body, tag, url, icon }) => {
  if (!isSupported() || Notification.permission !== 'granted' || isMuted()) return;
  const options = {
    body,
    tag,
    renotify: true,
    icon: icon || '/logo192.png',
    badge: '/logo192.png',
    data: { url: url || '/' },
  };
  // Prefer the service worker (required on Android, supports click routing via
  // the SW 'notificationclick' handler). Only when a SW actually controls the
  // page — otherwise (dev / no SW) fall back to the in-page Notification.
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.ready
      .then((reg) => reg.showNotification(title, options))
      .catch(() => fallbackNotification(title, options));
  } else {
    fallbackNotification(title, options);
  }
};

/**
 * Show a notification.
 * @param {string} message  toast text (and OS notification body)
 * @param {string} [title]  OS notification title (defaults to app name)
 * @param {'success'|'error'|'warning'|'info'} [severity]  toast colour
 * @param {boolean} [system]  also fire an OS notification when the tab is hidden
 * @param {string} [tag]  collapse key so repeats replace instead of stack
 * @param {string} [url]  route to open when the OS notification is clicked
 * @param {number} [duration]  toast auto-hide ms
 */
// Collapse duplicate notifications fired in quick succession (e.g. a component
// that calls notify() AND the global axios interceptor notifying the same
// success/error). Keyed on severity+message within a short window.
let _lastNotif = { key: '', t: 0 };

export const notify = ({ message, title, severity = 'info', system = false, tag, url, duration } = {}) => {
  if (!message) return;
  const dedupeKey = `${severity}:${message}`;
  const now = Date.now();
  if (dedupeKey === _lastNotif.key && now - _lastNotif.t < 2500) return; // duplicate — skip
  _lastNotif = { key: dedupeKey, t: now };
  const toast = { message, severity, duration, key: `${Date.now()}-${Math.round(Math.random() * 1e6)}` };
  if (toastHandler) toastHandler(toast);
  else pendingToasts.push(toast);

  if (system && (typeof document === 'undefined' || document.hidden)) {
    showSystemNotification({ title: title || 'Reseller Bazzar Admin', body: message, tag, url });
  }
};

export default notify;
